import { observer } from 'mobx-react-lite'
import { useDocumentTitle } from '@/hooks/document-title'
import { useFeatures } from '@/contexts/root-store-context'
import { useActivation } from '@/hooks/view-model-hooks'
import {
  DocumentsView,
  DocumentTableRow,
} from '@/features/docs/DocumentsView/index'
import { SkeletonListInfiniteScrollTrigger } from '@/components/SkeletonListInfiniteScrollTrigger'
import { DocumentActionsDropdownContainer } from '@/features/docs/components/DocumentActionsDropdownContainer'
import type { DocumentViewModel } from '@/features/docs/DocumentViewModel'

/**
 * Documents View Container
 */
export default observer(function DocumentsViewContainer() {
  useDocumentTitle('Documents')

  const {
    internal: { documentsVm: vm },
  } = useFeatures(({ docs }) => ({
    internal: docs.internal(),
  }))

  useActivation(vm)
  return (
    <DocumentsView
      searchTextValue={vm.listVm.searchText.value}
      onSearchTextChange={vm.listVm.searchText.change}
      onClearSearchText={vm.listVm.searchText.clear}
      emptyState={
        vm.listVm.emptyState === 'NoDocuments'
          ? 'no-documents'
          : vm.listVm.emptyState === 'NoSearchResults'
            ? 'no-search-results'
            : null
      }
    >
      {vm.listVm.filteredDocuments.map((dvm) => (
        <DocumentTableRowContainer vm={dvm} key={dvm.doc.longId} />
      ))}
      <SkeletonListInfiniteScrollTrigger
        scroller={vm.listVm.scroller}
        maxSkeletons={10}
      >
        <DocumentTableRow
          isTypeNameSkeleton
          isTypeImageSkeleton
          isJobNamesSkeleton
          isUserSkeleton
        />
      </SkeletonListInfiniteScrollTrigger>
    </DocumentsView>
  )
})

interface DocumentTableRowContainerProps {
  vm: DocumentViewModel
}

/**
 * Document Table Row Container
 */
export const DocumentTableRowContainer = observer(
  function DocumentTableRowContainer({ vm }: DocumentTableRowContainerProps) {
    return (
      <DocumentTableRow
        createdAt={vm.doc.dateCreated}
        title={vm.doc.title}
        authorName={vm.author?.isCurrentUser ? 'you' : vm.author?.displayName}
        typeName={vm.displayTypeName}
        typeImageUrl={vm.doc.typeImageUrl}
        isUserSkeleton={!vm.fetchUser.resolved}
        isJobNamesSkeleton={!vm.fetchJobs.resolved}
        isTypeImageSkeleton={!vm.fetchType.resolved}
        isTypeNameSkeleton={!vm.fetchType.resolved}
        jobNames={vm.jobs.map((j) => j.title)}
        actions={<DocumentActionsDropdownContainer vm={vm} />}
      />
    )
  },
)
